import { TAppLanguageISOCountryCode } from '@aqx-core/common/i18n';

export enum EMainProductCodeType {
  COMPANY = 'COMPANY',
  METATRADER4 = 'METATRADER4',
  FTX = 'FTX',
  METATRADER5 = 'METATRADER5',
  HORIZON = 'HORIZON',
  CENTROID = 'CENTROID',
  GENERIC = 'GENERIC'
}

export enum ESubProductCodeType {
  NEWS = 'NEWS',
  CALENDAR = 'CALENDAR',
  SIGNAL = 'SIGNAL',
  COPY_TRADE = 'COPY_TRADE',
  DEALER = 'DEALER',
  EDUCATION = 'EDUCATION',
  TRADING_VIEW = 'TRADING_VIEW'
}

export enum EMarketTimeManagementType {
  CLIENT = 'CLIENT',
  SERVER = 'SERVER'
}

export enum EFeatureListType {
  ACCOUNT_CLOSURE = 'ACCOUNT_CLOSURE',
  RESET_PASSWORD = 'RESET_PASSWORD',
  CHANGE_PASSWORD = 'CHANGE_PASSWORD',
  CUSTOMER_SERVICE = 'CUSTOMER_SERVICE',
  TOP_GAINER = 'TOP_GAINER',
  TOP_LOSER = 'TOP_LOSER',
  TOP_PICK = 'TOP_PICK',
  PIE_CHART = 'PIE_CHART',
  EXPERIENCE = 'EXPERIENCE',
  REGISTRATION = 'REGISTRATION',
  LADDER_TRADING = 'LADDER_TRADING',
  FORGOT_PASSWORD = 'FORGOT_PASSWORD'
}

export enum ELoginConfigType {
  COMPANY = 'COMPANY',
  METATRADER4_LIVE = 'METATRADER4_LIVE',
  METATRADER4_DEMO = 'METATRADER4_DEMO',
  METATRADER5_LIVE = 'METATRADER5_LIVE',
  METATRADER5_DEMO = 'METATRADER5_DEMO',
  METATRADER4_METATRADER5_DEMO = 'METATRADER4_METATRADER5_DEMO',
  METATRADER4_METATRADER5_LIVE = 'METATRADER4_METATRADER5_LIVE'
}

export enum EAssetType {
  CAROUSEL = 'CAROUSEL',
  CATEGORY = 'CATEGORY',
  SPLASH = 'SPLASH',
  TNC = 'TNC',
  BANNER_DEPOSIT = 'BANNER_DEPOSIT',
  BANNER_ACCOUNT_LIVE = 'BANNER_ACCOUNT_LIVE',
  BANNER_ACCOUNT_DEMO = 'BANNER_ACCOUNT_DEMO',
  FIREBASE_KEY_FILE = 'FIREBASE_KEY_FILE',
  FEATURE_ANNOUNCEMENT_CONTENT = 'FEATURE_ANNOUNCEMENT_CONTENT',
  FEATURE_ANNOUNCEMENT_ICON = 'FEATURE_ANNOUNCEMENT_ICON',
  COMPANY_ASSET = 'COMPANY_ASSET'
}

export enum ECrmChangePasswordType {
  FORM = 'FORM',
  INTEGRATION = 'INTEGRATION'
}

export enum ECompanySubscriptionType {
  FREEMIUM = 'FREEMIUM',
  PREMIUM = 'PREMIUM'
}

export enum ECompanyConfigEnabledFlagListType {
  DEMO_ACCOUNT_CREATION = 'DEMO_ACCOUNT_CREATION',
  ONE_CLICK_TRADING = 'ONE_CLICK_TRADING',
  EDUCATIONAL_VIDEO = 'EDUCATIONAL_VIDEO',
  SIGNAL = 'SIGNAL',
  PRETRADE_DETAILS = 'PRETRADE_DETAILS',
  ESTIMATED_MARGIN_REQUIRED = 'ESTIMATED_MARGIN_REQUIRED',
  TRADING_VIEW = 'TRADING_VIEW'
}

export enum EWebtraderResetPasswordType {
  INTEGRATION = 'INTEGRATION',
  REDIRECTION = 'REDIRECTION'
}

export enum EAppPlatform {
  ANDROID = 'ANDROID',
  IOS = 'IOS'
}

export enum EAppThemeMode {
  DARK = 'DARK',
  LIGHT = 'LIGHT'
}

// Config APIs params
export interface IAssetParams {
  assetType?: EAssetType;
}

// Config APIs response
export interface IAnnouncement {
  announcement: string;
  announcementHeader: string | null;
  publishDate: Date;
  url: string;
}

export interface IAsset {
  assetPath: string;
  assetType: EAssetType;
  publishDate: number;
  sequence: number;
  url: string;
  themeColour?: EAppThemeMode;
}

export interface ICompany {
  name: string;
  themeColourCode: string;
  profitColourCode: string;
  lossColourCode: string;
  tenantId: string;
  logoPath: string;
  logoDarkThemePath: string;
  faviconPath: string;
  shortcutIconPath: string;
  webtraderMemberTncUrl: string;
  webtraderMemberLogoutUrl: string;
  webtraderMemberPolicyUrl: string;
  webtraderMemberRiskDisclosureUrl: string;
  iosMobileAppUrl: string;
  androidMobileAppUrl: string;
  defaultLanguage: TAppLanguageISOCountryCode | null;
  marketTime: boolean;
  marketTimeManagement: EMarketTimeManagementType;
  fundManagement: boolean;
  featureList: EFeatureListType[];
  mainProductCodeList: EMainProductCodeType[];
  subProductCodeList: ESubProductCodeType[];
  loginConfigList: ELoginConfigType[];
  email: string;
  mt4DemoRegistrationUrl: string;
  mt4LiveRegistrationUrl: string;
  mt4LiveRegistrationIosUrl: string;
  mt4DemoRegistrationIosUrl: string;
  mt4LiveRegistrationAndroidUrl: string;
  mt4DemoRegistrationAndroidUrl: string;
  mt5LiveRegistrationUrl: string;
  mt5DemoRegistrationUrl: string;
  mt5LiveRegistrationIosUrl: string;
  mt5DemoRegistrationIosUrl: string;
  mt5LiveRegistrationAndroidUrl: string;
  mt5DemoRegistrationAndroidUrl: string;
  companyRegistrationUrl: string;
  crmChangePasswordType: ECrmChangePasswordType;
  crmForgotPasswordType: EWebtraderResetPasswordType;
  crmForgotPasswordUrl: string;
  transactionFee: number;
  serverDlsCorrection: boolean;
  serverTimezone: number;
  tncPath: string;
  oneClickTradingEnabled: boolean;
  demoAccountCreationEnabled: boolean;
  webtraderResetPasswordType: EWebtraderResetPasswordType;
  webtraderResetPasswordUrl: string;
  webtraderContactSupportUrl: string;
  educationalVideoEnabled: boolean;
  companySubscriptionList: ICompanySubscription[];
  signalEnabled: boolean;
  companyConfigEnabledFlagList: ECompanyConfigEnabledFlagListType[];
  multiServerEnabled: boolean;
  otpEnabled: boolean;
  currentMainProductCode: EMainProductCodeType;
  supportedLanguages: TAppLanguageISOCountryCode[];
}

export interface IContact {
  website: string;
  email: string;
  socialMedia: Record<string, string>;
  dialCode: string;
  mobile: number;
  businessHoursTimeZone: string;
  businessHours: IBusinessHours[];
  lastUpdated: Date;
}

export interface IBusinessHours {
  dayOfWeek: number;
  from?: string;
  to?: string;
}

export interface ICompanySubscription {
  productCode: ESubProductCodeType;
  productSubscription: ECompanySubscriptionType;
}

export interface IAppVersionParams {
  device: EAppPlatform;
}

export interface IAppVersion {
  currentVersion: string;
  device: EAppPlatform;
  minVersion: string;
  releaseDate?: number;
  updateUrl: string;
}

export interface IGetAnnouncementsParams {
  language?: string;
}

export enum EFeatureAnnouncementPlatform {
  APP = 'APP',
  WEB = 'WEB',
  OVERALL = 'OVERALL'
}

export enum EFeatureAnnouncementStatus {
  ACTIVE = 0,
  DRAFT = 1,
  DELETED = 2,
  EXPIRED = 3
}

export interface IFeatureAnnouncementAsset {
  id: string;
  fileName: string;
  s3FullName: string;
  s3FullPath: string;
}

interface IFeatureAnnouncementDescription {
  language: string;
  descriptions: string[];
  header: string;
}

interface IFeatureAnnouncementContent {
  featureMediaList: IFeatureAnnouncementAsset[];
  featureIconList: IFeatureAnnouncementAsset[];
  descriptionList: IFeatureAnnouncementDescription[];
  duration?: number;
}

export enum EFeatureAnnouncementAccountType {
  LIVE = 'LIVE',
  DEMO = 'DEMO'
}

export interface IFeatureAnnouncement {
  id: string;
  feature: string;
  platform: EFeatureAnnouncementPlatform[];
  content: IFeatureAnnouncementContent;
  url: string;
  tenants: string[];
  createdTime: number;
  updatedTime: number;
  publishedTime: number;
  expiredTime: number;
  deletedTime: number;
  status: EFeatureAnnouncementStatus;
  accountTypes: EFeatureAnnouncementAccountType[];
}

export interface IUpdateAnnouncementParams {
  id: IFeatureAnnouncement['id'];
}
